import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import KTTooltip from '../../../components/KTTooltip';
import { ANALYTICS_PROVIDER_COMPARISON_COLOR_CODE } from '../../../config/constants/analytics';
import { PROVIDERS } from '../../../config/env';
import { isEmpty } from '../../../utils';
import { formatDateExact } from '../../../utils/format-date';
import { readablePrice } from '../../../utils/getCurrency';
import * as selectors from '../selectors';

const OrderTableRow = ({
  orderData,
  branches,
  analyticsProviders,
  analyticsOrderStatus,
  hideOrderSourcePill,
}) => {
  const {
    id,
    external_id: externalId,
    created_at: orderCreationTime,
    delivered_at: deliveredAt,
    brand_title: brandTitle,
    branch_id: branchId,
    providerID,
    status: cellStatus,
    status_type: statusType,
    item_price: itemPrice,
    delivery_fee: deliveryFee,
    final_price: finalPrice,
    discount,
    discount_breakdown: discountBreakdownRaw,
    item_count: orderedItems,
    order_source: orderSource,
    currency_symbol: currencySymbol,
    currency: currencyCode,
    cancellation_reason: cancellationReason,
    cancelled_by: cancelledBy,
  } = orderData || {};

  const discountBreakdown = discountBreakdownRaw || {
    merchant_discount: 0,
    provider_discount: 0,
    custom_discount: 0,
    delivery_fee_discount: 0,
  };

  const currencyObj = { code: currencyCode, symbol: currencySymbol };

  const getTitleForEntity = (items, itemId) => {
    const tableCell =
      items && items.length && itemId
        ? items.find(item => item.id === itemId)
        : null;

    const { title } = tableCell || {};
    return title;
  };

  const getStatusTitle = statusId => {
    const tableCell =
      analyticsOrderStatus && analyticsOrderStatus.length && statusId
        ? analyticsOrderStatus.find(s => s.id === statusId)
        : null;

    const { status } = tableCell || {};

    return status;
  };
  const displayStatus = getStatusTitle(cellStatus) || statusType || '–';
  const renderOrderSourceType = providerId => {
    if (hideOrderSourcePill) return null;
    if (providerId === PROVIDERS.KLIKIT) {
      return !isEmpty(orderSource) ? (
        <small
          className="text-center capitalize ml-1 border rounded-full  border-grey-dark p-1"
          style={{ width: '75px' }}
        >
          {' '}
          {orderSource}
        </small>
      ) : null;
    }
    return null;
  };

  const sliceExternalId = idToSlice => {
    if (idToSlice == null || idToSlice === '') return '–';
    const s = String(idToSlice);
    if (s.length <= 7) return s;
    return `${s.slice(0, 3)}...${s.slice(-4)}`;
  };

  const discountBreakdownEl = (() => {
    const breakdownEl = (
      <div className="flex flex-col gap-y-1 w-40">
        <div className="flex justify-between">
          <span>Merchant Discount</span>
          <span>
            {readablePrice(discountBreakdown.merchant_discount, currencyObj)}
          </span>
        </div>
        <div className="flex justify-between">
          <span>Provider Discount</span>
          <span>
            {readablePrice(discountBreakdown.provider_discount, currencyObj)}
          </span>
        </div>
        <div className="flex justify-between">
          <span>Custom Discount</span>
          <span>
            {readablePrice(discountBreakdown.custom_discount, currencyObj)}
          </span>
        </div>
        <div className="flex justify-between">
          <span>Delivery Fee Discount</span>
          <span>
            {readablePrice(
              discountBreakdown.delivery_fee_discount,
              currencyObj,
            )}
          </span>
        </div>
      </div>
    );

    return (
      <KTTooltip content={breakdownEl} position={['right']}>
        <i className="kt-info text-lg text-grey-darker cursor-pointer" />
      </KTTooltip>
    );
  })();

  return (
    <tr className="border-b border-grey hover:bg-grey-lighter">
      <td className="h-10 px-2 text-left">{id}</td>
      <td className="h-10 px-2 text-left">{sliceExternalId(externalId)}</td>
      <td className="h-10 px-2 text-left">
        {formatDateExact(orderCreationTime)}
      </td>
      <td className="h-10 px-2 text-left">
        {deliveredAt ? formatDateExact(deliveredAt) : '–'}
      </td>
      <td className="h-10 px-2 text-left">{brandTitle}</td>
      <td className="h-10 px-2 text-left">
        {getTitleForEntity(branches, branchId)}
      </td>
      <td className="h-10 px-2 text-left">
        <div className=" flex items-center justify-center gap-x-1">
          <div
            className="w-2 h-2 rounded-full mr-1"
            style={{
              background: ANALYTICS_PROVIDER_COMPARISON_COLOR_CODE[providerID],
            }}
          />
          {getTitleForEntity(analyticsProviders, providerID)}
          {renderOrderSourceType(providerID)}
        </div>
      </td>
      <td className="h-10 px-2 text-left">{displayStatus}</td>
      <td className="sc-tabular-nums h-10 px-2 text-right">
        {orderedItems != null ? orderedItems : '–'}
      </td>
      <td className="sc-tabular-nums h-10 px-2 text-right">
        {readablePrice(itemPrice, currencyObj)}
      </td>
      <td className="sc-tabular-nums h-10 px-2 text-right">
        {readablePrice(deliveryFee, currencyObj)}
      </td>
      <td className="h-10 px-2 text-right flex gap-x-2">
        <div className="flex items-center justify-end w-full h-full gap-x-1">
          {readablePrice(discount, currencyObj)}
          {discountBreakdownEl}
        </div>
      </td>
      <td className="sc-tabular-nums h-10 px-2 text-right">
        {readablePrice(finalPrice, currencyObj)}
      </td>
      <td className="h-10 px-2 text-left">{cancellationReason || 'N/A'}</td>
      <td className="h-10 px-2 text-left">{cancelledBy || 'N/A'}</td>
    </tr>
  );
};

OrderTableRow.propTypes = {
  orderData: PropTypes.object,
  branches: PropTypes.array,
  analyticsProviders: PropTypes.array,
  analyticsOrderStatus: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]),
  hideOrderSourcePill: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  branches: selectors.makeSelectAnalyticsBranches(),
  analyticsProviders: selectors.makeSelectAnalyticsProviders(),
  analyticsOrderStatus: selectors.makeSelectStatus(),
});

const withConnect = connect(mapStateToProps);

export default compose(withConnect)(OrderTableRow);
